import React, { useEffect, useLayoutEffect } from "react";
import { useTranslationMemories } from "./TranslationMemoryHook";
import { TranslationMemory } from "./types";
import { SegmentationItem } from "./types";
import { Spinner } from "../utils/Spinner";

type Props = {
  focusedSegmentIndex: number;
  focusedSegment: SegmentationItem;
  sourceLanguage: string;
  targetLanguage: string;
  handleTranslationMemoryClick: (tm: TranslationMemory) => void;
  lastFocusedSegmentIndex: number;
  setLastFocusedSegmentIndex: (index: number) => void;
  getSimilarityColor: (similarity: number) => string;
};

export const TranslationViewTranslationMemoryLookupsSource: React.FC<Props> = ({
  focusedSegmentIndex,
  focusedSegment,
  sourceLanguage,
  targetLanguage,
  handleTranslationMemoryClick,
  lastFocusedSegmentIndex,
  setLastFocusedSegmentIndex,
  getSimilarityColor,
}) => {
  const getShortcutIndex = (index: number): string | null => {
    if (index < 9) {
      return `${index + 1}`;
    }
    return null;
  };
  const translationMemories = useTranslationMemories();

  useEffect(() => {
    if (lastFocusedSegmentIndex !== focusedSegmentIndex) {
      if (focusedSegment?.source_text) {
        setLastFocusedSegmentIndex(focusedSegmentIndex);
        translationMemories.search(
          focusedSegment.source_text,
          sourceLanguage,
          targetLanguage,
          focusedSegmentIndex,
          "source_text"
        );
      }
    }
  }, [
    focusedSegmentIndex,
    focusedSegment,
    sourceLanguage,
    targetLanguage,
    lastFocusedSegmentIndex,
  ]);

  useLayoutEffect(() => {
    const abortController = new AbortController();

    const cycleTranslationMemory = (direction: "up" | "down"): void => {
      const currentFocusedTMId = parseInt(
        document.activeElement?.getAttribute("data-translation-memory-id") ??
          "0"
      );
      const currentFocusedTMIndex = translationMemories
        .getAtIndex(focusedSegmentIndex)
        .findIndex((tm) => tm.id === currentFocusedTMId);
      const newIndex = Math.max(
        0,
        Math.min(
          currentFocusedTMIndex + (direction === "up" ? -1 : 1),
          translationMemories.getAtIndex(focusedSegmentIndex).length - 1
        )
      );
      if (
        newIndex >= 0 &&
        newIndex < translationMemories.getAtIndex(focusedSegmentIndex).length
      ) {
        const tm = translationMemories.getAtIndex(focusedSegmentIndex)[
          newIndex
        ];
        const element = document.querySelector<HTMLDivElement>(
          `[data-translation-memory-id="${tm?.id}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          element.focus();
        }
      }
    };
    const handleKeyDown = (e: KeyboardEvent): void => {
      const keyNumber = parseInt(e.key);
      if (!isNaN(keyNumber) && e.ctrlKey) {
        const tm = translationMemories.getAtIndex(focusedSegmentIndex)[
          keyNumber - 1
        ];
        if (tm) {
          handleTranslationMemoryClick(tm);
        }
      }
      if (e.key === "ArrowUp" && e.ctrlKey && e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        cycleTranslationMemory("up");
      }
      if (e.key === "ArrowDown" && e.ctrlKey && e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        cycleTranslationMemory("down");
      }
      if (e.key === "Enter" && e.ctrlKey && e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        const currentFocusedTMId = parseInt(
          document.activeElement?.getAttribute("data-translation-memory-id") ??
            "0"
        );
        const currentFocusedTMIndex = translationMemories
          .getAtIndex(focusedSegmentIndex)
          .findIndex((tm) => tm.id === currentFocusedTMId);

        const TM = translationMemories.getAtIndex(focusedSegmentIndex)[
          currentFocusedTMIndex
        ];
        if (TM) {
          handleTranslationMemoryClick(TM);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [focusedSegmentIndex, translationMemories]);

  return (
    <div>
      <div className="tw-flex tw-gap-2 tw-items-baseline tw-mb-0">
        <small className="tw-font-bold">Matching source text</small>
        {translationMemories.isSearching ? (
          <Spinner align="left" testId="translation-memory-spinner-source" />
        ) : (
          <span
            className="tw-text-gray-500 !tw-text-xs"
            data-testid="translation-memory-count-source"
          >
            {translationMemories.getAtIndex(focusedSegmentIndex).length} item(s)
          </span>
        )}
        <div className="tw-ml-auto tw-flex tw-gap-1">
          <kbd data-balloon-pos="up-right" data-balloon="Cycle through Lookups">
            <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>Arrow Up/Down</kbd>
          </kbd>{" "}
          <kbd
            data-balloon-pos="up-right"
            data-balloon="Use the highlighted Lookup"
          >
            <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>Enter</kbd>
          </kbd>
        </div>
      </div>
      <div
        className="tw-flex tw-flex-col tw-divide-y-2 tw-ring-2 tw-ring-gray-200 tw-rounded-md tw-p-2 tw-h-[150px] tw-overflow-y-auto tw-overscroll-contain tw-bg-white"
        data-testid="translation-memory-list-source"
      >
        {translationMemories.getAtIndex(focusedSegmentIndex).length === 0 && (
          <div
            className="tw-text-center tw-my-auto tw-text-gray-500"
            data-testid="translation-memory-empty-source"
          >
            {translationMemories.isSearching ? (
              <span
                className="tw-animate-pulse"
                data-testid="translation-memory-searching-source"
              >
                Searching Source...
              </span>
            ) : (
              `No Lookups found for segment ${focusedSegmentIndex + 1}`
            )}
          </div>
        )}

        {translationMemories
          .getAtIndex(focusedSegmentIndex)
          .map((tm, index) => (
            <div
              key={tm.id}
              className="tw-grid tw-gap-2 tw-grid-cols-2 hover:tw-bg-gray-100 tw-cursor-pointer tw-relative"
              onClick={() => handleTranslationMemoryClick(tm)}
              data-translation-memory-id={tm.id}
              data-testid={`translation-memory-item-source-${tm.id}`}
              tabIndex={index}
              onKeyDown={(e) => {
                if (e.key === `${index + 1}` && e.ctrlKey) {
                  handleTranslationMemoryClick(tm);
                }
              }}
            >
              <small className="tw-text-gray-500 tw-absolute tw-bg-white tw-rounded-md tw-p-1 -tw-top-[10px] tw-left-0 !tw-text-xs">
                ID {tm.id}
              </small>
              {getShortcutIndex(index) && (
                <kbd className="tw-absolute -tw-top-[8px] tw-right-0 ">
                  <kbd>Ctrl</kbd>+<kbd>{getShortcutIndex(index)}</kbd>
                </kbd>
              )}
              <p className="tw-bg-blue-100 tw-rounded-md tw-p-2 tw-text-sm">
                {tm.source_text}
              </p>{" "}
              <p className="tw-bg-blue-100 tw-rounded-md tw-p-2 tw-text-sm">
                <small
                  className={`tw-ring-2 tw-ring-green-200 tw-rounded-md tw-bg-green-500 tw-text-white tw-p-1 ${getSimilarityColor(
                    tm.similarity
                  )}`}
                >
                  {Math.round(tm.similarity * 100)}%
                </small>{" "}
                {tm.target_text}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};
